import React from "react";

// AAFCO-style pet-food label for the Pet Toppers mode. Renders a Guaranteed
// Analysis box, calorie-content statement, complementary-feeding designation,
// and the pet ingredient statement. This is a DRAFT preview — not a final,
// AAFCO-reviewed label.
export default function GuaranteedAnalysisLabel({ pet, brandName, productName, netWeight, businessProfile }) {
  if (!pet) return null;
  const ga = pet.guaranteed_analysis;
  const cal = pet.calorie_content;
  const speciesLabel = pet.species === "cat" ? "Cat" : "Dog";

  // Responsible-party ("Distributed by / Manufactured by") block — AAFCO
  // requires the name + address of the manufacturer or distributor.
  const bp = businessProfile || {};
  const rpPrefix = bp.responsible_party_prefix || "Distributed by";
  const rpName = bp.legal_entity || bp.brand_name || "";
  const cityLine = [bp.city, bp.state].filter(Boolean).join(", ");
  const cityStateZip = [cityLine, bp.zip_code].filter(Boolean).join(" ").trim();
  const addressLines = [bp.address_line1, bp.address_line2, cityStateZip, bp.country]
    .map((s) => (s || "").trim())
    .filter(Boolean);
  const hasResponsibleParty = rpName || addressLines.length > 0;

  return (
    <div className="font-fda bg-white text-black w-full max-w-[320px] border-[3px] border-black select-none"
      data-testid="guaranteed-analysis-label">
      {/* Brand header */}
      {(brandName || productName) && (
        <div className="px-2 pt-2 pb-1 text-center border-b-2 border-black">
          {brandName && <div className="text-[14px] font-black tracking-tight">{brandName.toUpperCase()}</div>}
          {productName && <div className="text-[12px] font-bold">{productName}</div>}
          <div className="text-[10px] mt-0.5">
            {speciesLabel} Topper · Complementary Treat
            {netWeight ? ` · NET WT ${netWeight}` : ""}
          </div>
        </div>
      )}

      <div className="p-2">
        <div className="text-[18px] font-black leading-none tracking-tight">Guaranteed Analysis</div>
        <div className="border-b-4 border-black mt-1" />
        {ga?.rows?.map((row) => (
          <div key={row.label} className="flex justify-between border-b border-black py-0.5 text-[13px]">
            <span className="font-bold">{row.label}</span>
            <span>
              <span className="text-[10px] uppercase mr-1 text-stone-600">
                ({row.qualifier === "min" ? "min" : "max"})
              </span>
              {row.pct}%
            </span>
          </div>
        ))}

        {/* Calorie content statement — AAFCO requires kcal ME */}
        {cal && (
          <div className="mt-2 text-[10px] leading-tight">
            <div className="font-bold">Calorie Content (calculated, ME):</div>
            <div>{cal.kcal_per_kg.toLocaleString()} kcal/kg</div>
            <div>{cal.kcal_per_serving} kcal per {cal.serving_size_g} g serving</div>
          </div>
        )}

        {/* Feeding directions — "how much to add" by body weight (10% rule) */}
        {pet.feeding_directions?.rows?.length > 0 && (
          <div className="mt-2 pt-2 border-t-2 border-black text-[9px] leading-tight">
            <div className="font-bold text-[10px]">Feeding Directions</div>
            <div className="mt-0.5">{pet.feeding_directions.rule}</div>
            <table className="w-full mt-1 border-collapse">
              <thead>
                <tr className="border-b border-black">
                  <th className="text-left font-bold py-0.5">
                    {pet.species === "cat" ? "Cat" : "Dog"} weight
                  </th>
                  <th className="text-right font-bold py-0.5">Max per day</th>
                </tr>
              </thead>
              <tbody>
                {pet.feeding_directions.rows.map((r) => (
                  <tr key={r.weight_lb} className="border-b border-stone-300">
                    <td className="py-0.5">{r.weight_lb} lb</td>
                    <td className="text-right py-0.5">{r.max_g_per_day} g</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {pet.feeding_directions.note && (
              <div className="mt-1 italic text-stone-600">{pet.feeding_directions.note}</div>
            )}
          </div>
        )}

        {/* Feeding designation — complementary / supplemental */}
        {pet.feeding_designation && (
          <div className="mt-2 pt-2 border-t-2 border-black text-[9.5px] font-bold leading-tight">
            {pet.feeding_designation}
          </div>
        )}

        {/* Ingredient statement */}
        {pet.ingredient_statement && (
          <div className="mt-2 pt-2 border-t border-black text-[9px] leading-tight">
            <span className="font-bold">Ingredients: </span>{pet.ingredient_statement}
          </div>
        )}

        {/* Net weight statement — AAFCO required */}
        <div className="mt-2 pt-2 border-t-2 border-black text-[11px] font-black text-center">
          NET WT {netWeight || "____"}
        </div>

        {/* Responsible-party block — AAFCO required (name + address) */}
        <div className="mt-2 pt-2 border-t border-black text-[9px] leading-tight">
          {hasResponsibleParty ? (
            <>
              <div className="font-bold">{rpPrefix}:</div>
              {rpName && <div>{rpName}</div>}
              {addressLines.map((line, i) => (
                <div key={i}>{line}</div>
              ))}
              {bp.website && <div>{bp.website}</div>}
            </>
          ) : (
            <div className="italic text-stone-500">
              {rpPrefix}: [add your business name &amp; address in Settings → Business Profile]
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
